import React from 'react';
import { apiClient } from '../lib/api/client';
import { UiPreferences } from '../lib/types/api';

const defaultPreset = {
  menu: { base: '#d41616' },
  background: { base: '#0F0F0F' },
  buttons: { base: '#00D4FF' },
  text: { primary: '#F8FAFC', secondary: '#E2E8F0' },
};

const ThemeAppearance: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [prefs, setPrefs] = React.useState<UiPreferences>({});

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await apiClient.getUiPreferences();
        if (!mounted) return;
        setPrefs({ ...defaultPreset, ...data });
      } catch {
        setPrefs(defaultPreset);
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleChange = (path: string, value: string) => {
    setPrefs(prev => {
      const next = { ...prev } as any;
      const [top, key] = path.split('.');
      next[top] = { ...(next[top] || {}), [key]: value };
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.updateUiPreferences(prefs);
    } finally {
      setSaving(false);
    }
  };

  // Live preview via CSS variables for menu only (others can be expanded similarly)
  const menuColor = (prefs.menu as any)?.base || '#d41616';
  const [r, g, b] = (() => {
    const hex = menuColor.replace('#','');
    const n = hex.length === 3 ? hex.split('').map((ch: string) => ch + ch).join('') : hex;
    const rr = parseInt(n.slice(0,2), 16);
    const gg = parseInt(n.slice(2,4), 16);
    const bb = parseInt(n.slice(4,6), 16);
    return [rr, gg, bb];
  })();
  const rgba = (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;
  const customMenuVars: React.CSSProperties = {
    ['--menu-bg-start' as any]: rgba(0.28),
    ['--menu-bg-end' as any]: rgba(0.12),
    ['--menu-border' as any]: rgba(0.30),
    ['--menu-active-start' as any]: rgba(0.36),
    ['--menu-active-end' as any]: rgba(0.18),
    ['--menu-active-border' as any]: rgba(0.55),
    ['--menu-shadow' as any]: `0 10px 25px ${rgba(0.22)}`,
  };

  if (loading) return <div className="p-6 text-text-enterprise-primary">Loading preferences...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-display-sm font-bold gradient-text">Theme & Appearance</h1>
      <p className="text-text-enterprise-secondary">Customize your UI colors. Dark mode and glassmorphism are preserved.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="glass-card p-4 space-y-4">
          <h2 className="text-heading-md font-semibold text-text-enterprise-primary">Menu</h2>
          <div className="flex items-center gap-3">
            <label className="text-sm text-text-enterprise-secondary">Base</label>
            <input type="color" value={(prefs.menu as any)?.base || '#d41616'} onChange={(e) => handleChange('menu.base', e.target.value)} className="h-7 w-10 p-0 bg-transparent border-0 cursor-pointer" />
          </div>
          <div className="mt-4">
            <div className="glass-menu p-2 space-y-1" style={customMenuVars}>
              {["Menu A","Menu B","Menu C"].map((label) => (
                <button key={label} className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 backdrop-blur-md glass-menu-item menu-variant-custom`}>
                  <span className="flex items-center gap-3">
                    <span className="w-5 h-5 rounded bg-white/20" />
                    <span>{label}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="glass-card p-4 space-y-4">
          <h2 className="text-heading-md font-semibold text-text-enterprise-primary">Background</h2>
          <div className="flex items-center gap-3">
            <label className="text-sm text-text-enterprise-secondary">Base</label>
            <input type="color" value={(prefs.background as any)?.base || '#0F0F0F'} onChange={(e) => handleChange('background.base', e.target.value)} className="h-7 w-10 p-0 bg-transparent border-0 cursor-pointer" />
          </div>
          <div className="p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${(prefs.background as any)?.base || '#0F0F0F'} 0%, #141414 50%, #1A1A1A 100%)` }}>
            <div className="text-text-enterprise-primary">Preview content</div>
          </div>
        </div>

        <div className="glass-card p-4 space-y-4">
          <h2 className="text-heading-md font-semibold text-text-enterprise-primary">Buttons</h2>
          <div className="flex items-center gap-3">
            <label className="text-sm text-text-enterprise-secondary">Primary</label>
            <input type="color" value={(prefs.buttons as any)?.base || '#00D4FF'} onChange={(e) => handleChange('buttons.base', e.target.value)} className="h-7 w-10 p-0 bg-transparent border-0 cursor-pointer" />
          </div>
          <div className="flex gap-3">
            <button className="glass-button px-4 py-2 rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${(prefs.buttons as any)?.base || '#00D4FF'}33 0%, #7C3AED33 100%)` }}>Primary</button>
            <button className="glass-button px-4 py-2 rounded-xl text-white opacity-70" style={{ background: `linear-gradient(135deg, ${(prefs.buttons as any)?.base || '#00D4FF'}22 0%, #7C3AED22 100%)` }}>Ghost</button>
          </div>
        </div>

        <div className="glass-card p-4 space-y-4">
          <h2 className="text-heading-md font-semibold text-text-enterprise-primary">Text & Accents</h2>
          <div className="flex items-center gap-3">
            <label className="text-sm text-text-enterprise-secondary">Primary</label>
            <input type="color" value={(prefs.text as any)?.primary || '#F8FAFC'} onChange={(e) => handleChange('text.primary', e.target.value)} className="h-7 w-10 p-0 bg-transparent border-0 cursor-pointer" />
          </div>
          <div className="space-y-2">
            <div className="text-lg" style={{ color: (prefs.text as any)?.primary || '#F8FAFC' }}>Heading</div>
            <div className="text-sm" style={{ color: (prefs.text as any)?.secondary || '#E2E8F0' }}>Secondary text</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button disabled={saving} onClick={save} className="glass-button px-4 py-2 rounded-xl text-white">
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
};

export default ThemeAppearance;



